import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Badge,
  Avatar,
  IconButton,
  useToast,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Flex,
  Spinner,
  Collapse,
  Tooltip,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay
} from '@chakra-ui/react';
import { AddIcon, SearchIcon, CloseIcon } from '@chakra-ui/icons';
import { useParams, useHistory } from 'react-router-dom';
import {
  fetchBoardLists,
  fetchListCards,
  getCardDetails,
  linkRelatedCard,
} from '../../../../ProjectBoardServices/BoardModalAPI/RightModalAPI/LinkTabAPI';

const LinkTab = ({ task, currentBoardId }) => {
  const toast = useToast();
  const history = useHistory();
  const { boardId } = useParams();

  const [linkedItems, setLinkedItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Inline add card states
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [boards, setBoards] = useState([]);
  const [filteredBoards, setFilteredBoards] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState('');
  const [availableCards, setAvailableCards] = useState([]);
  const [filteredCards, setFilteredCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState('');
  const [searchBoardQuery, setSearchBoardQuery] = useState('');
  const [searchCardQuery, setSearchCardQuery] = useState('');
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [loadingCards, setLoadingCards] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const cancelRef = useRef();

  const statusColors = {
    active: { bg: '#DBEAFE', color: '#1E40AF', dot: '#3B82F6', label: 'Active' },
    completed: { bg: '#D1FAE5', color: '#065F46', dot: '#10B981', label: 'Completed' },
    inactive: { bg: '#F3F4F6', color: '#6B7280', dot: '#9CA3AF', label: 'Inactive' },
    blocked: { bg: '#FEE2E2', color: '#991B1B', dot: '#EF4444', label: 'Blocked' },
  };

  useEffect(() => {
    fetchBoards();
  }, []);

  useEffect(() => {
    if (task?.id || task?.originalId || task?._id) {
      loadLinkedCards();
    }
  }, [task]);

  useEffect(() => {
    if (searchBoardQuery) {
      const filtered = boards.filter((board) =>
        board.title.toLowerCase().includes(searchBoardQuery.toLowerCase())
      );
      setFilteredBoards(filtered);
    } else {
      setFilteredBoards(boards);
    }
  }, [searchBoardQuery, boards]);

  useEffect(() => {
    if (searchCardQuery) {
      const filtered = availableCards.filter((card) =>
        card.title.toLowerCase().includes(searchCardQuery.toLowerCase())
      );
      setFilteredCards(filtered);
    } else {
      setFilteredCards(availableCards);
    }
  }, [searchCardQuery, availableCards]);

  const fetchBoards = async () => {
    setLoadingBoards(true);
    try {
      const boardIdToUse = task?.boardId || currentBoardId || '68905b380c869542b3016012';
      // ✅ Service function use kar rahe hain
      const response = await fetchBoardLists(boardIdToUse);

      // console.log('Boards API response:', response.data);
      if (response.data?.status && response.data?.data) {
        const listsAsBoards = response.data.data.map((list) => ({
          id: list.id,
          title: list.title,
          description: list.description || '',
          boardId: list.board_id,
        }));
        setBoards(listsAsBoards);
        setFilteredBoards(listsAsBoards);
      } else {
        throw new Error('Invalid boards API response');
      }
    } catch (error) {
      console.error('Error fetching boards:', error);
      toast({
        title: 'Error loading lists',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingBoards(false);
    }
  };

  const fetchCardsForBoard = async (listId) => {
    if (!listId) return;

    setLoadingCards(true);
    try {
      // ✅ Service function use kar rahe hain
      const cardsResponse = await fetchListCards(listId);

      // console.log('Cards API response:', cardsResponse.data);
      if (cardsResponse.data?.status && Array.isArray(cardsResponse.data?.data)) {
        const allCards = cardsResponse.data.data;
        const currentTaskId = task?.id || task?.originalId || task?._id;
        const selectedList = boards.find((b) => b.id === listId);
        const listTitle = selectedList?.title || '';
        const filtered = allCards
          .filter(
            (card) =>
              card.id !== currentTaskId && !linkedItems.some((linked) => linked.id === card.id)
          )
          .map((card) => ({ ...card, listTitle }));

        // console.log('Filtered cards:', filtered);
        setAvailableCards(filtered);
        setFilteredCards(filtered);
      } else {
        throw new Error('Invalid cards API response');
      }
    } catch (error) {
      console.error('Error fetching cards:', error);
      toast({
        title: 'Error loading cards',
        description: error.message || 'Failed to load cards',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setAvailableCards([]);
      setFilteredCards([]);
    } finally {
      setLoadingCards(false);
    }
  };

  const loadLinkedCards = async () => {
    setLoading(true);
    try {
      if (task?.relatedCards && Array.isArray(task.relatedCards) && task.relatedCards.length > 0) {
        const cardDetailsPromises = task.relatedCards.map(async (cardId) => {
          try {
            // ✅ Service function use kar rahe hain
            const response = await getCardDetails(cardId);

            // console.log(`Card ${cardId} response:`, response.data);
            if (response.data?.status && response.data?.data) {
              const card = response.data.data;
              return {
                id: card.id,
                title: card.title,
                status: card.status || 'active',
                assignee: {
                  name: card.assignee?.name || 'Unassigned',
                  avatar: null,
                  initials: card.assignee?.name?.substring(0, 2).toUpperCase() || 'UN',
                  bgColor: getRandomColor(),
                },
                type: card.type || 'task',
                listTitle: '',
                boardId: card.board_id,
              };
            }
            return null;
          } catch (error) {
            console.error(`Error fetching card ${cardId}:`, error);
            return null;
          }
        });

        const cardDetails = await Promise.all(cardDetailsPromises);
        const validCards = cardDetails.filter((card) => card !== null);
        // console.log('Loaded linked cards:', validCards);
        setLinkedItems(validCards);
      } else {
        setLinkedItems([]);
      }
    } catch (error) {
      console.error('Error loading linked cards:', error);
      setLinkedItems([]);
    } finally {
      setLoading(false);
    }
  };

  const getRandomColor = () => {
    const colors = ['blue.500', 'green.500', 'purple.500', 'orange.500', 'teal.500', 'pink.500'];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const handleBoardSelect = (boardId) => {
    setSelectedBoard(boardId);
    setSelectedCard('');
    setAvailableCards([]);
    setFilteredCards([]);
    setSearchCardQuery('');

    if (boardId) {
      fetchCardsForBoard(boardId);
    }
  };

  const handleLinkCard = () => {
    if (!selectedCard) {
      toast({
        title: 'Please select a card',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    setIsAlertOpen(true);
  };

  const confirmLinkCard = async () => {
    setIsAlertOpen(false);

    const currentTaskId = task?.id || task?.originalId || task?._id;
    if (!currentTaskId) {
      toast({
        title: 'Error',
        description: 'Current task ID not found',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // ✅ Service function use kar rahe hain
      const response = await linkRelatedCard(currentTaskId, selectedCard);

      // console.log('Link card response:', response.data);
      if (response.data?.status) {
        const linkedCard = availableCards.find((card) => card.id === selectedCard);

        if (linkedCard) {
          const newLinkedItem = {
            id: linkedCard.id,
            title: linkedCard.title,
            status: linkedCard.status || 'active',
            assignee: {
              name: linkedCard.assignee?.name || 'Unassigned',
              avatar: null,
              initials: linkedCard.assignee?.name?.substring(0, 2).toUpperCase() || 'UN',
              bgColor: getRandomColor(),
            },
            type: linkedCard.type || 'task',
            listTitle: linkedCard.listTitle || '',
            boardId: linkedCard.board_id,
          };

          setLinkedItems((prev) => [...prev, newLinkedItem]);
        }

        toast({
          title: 'Card linked successfully',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });

        setSelectedBoard('');
        setSelectedCard('');
        setAvailableCards([]);
        setFilteredCards([]);
        setSearchCardQuery('');
        setSearchBoardQuery('');
        setIsAddingCard(false);
      } else {
        throw new Error('Invalid link card response');
      }
    } catch (error) {
      console.error('Error linking card:', error);
      toast({
        title: 'Error linking card',
        description: error.response?.data?.message || 'Failed to link the card',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCardClick = (cardId) => {
    const currentUrl = window.location.pathname;
    const newUrl = currentUrl.replace(/\/task\/[^/]+$/, `/task/${cardId}`);
    window.open(newUrl, '_blank');
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="200px">
        <Spinner size="lg" color="blue.500" />
      </Flex>
    );
  }

  const selectedCardTitle =
    availableCards.find((card) => card.id === selectedCard)?.title || '';

  return (
    <VStack align="stretch" spacing={4} bg="white" p={4}>
      {/* Header */}
      <Flex justify="space-between" align="center">
        <HStack spacing={2}>
          <Text fontSize="15px" fontWeight="600" color="#1F2937">
            Linked Items
          </Text>
          <Badge
            bg="#F3F4F6"
            color="#6B7280"
            fontSize="11px"
            px={2}
            py={1}
            borderRadius="full"
            fontWeight="600"
          >
            {linkedItems.length}
          </Badge>
        </HStack>

        {!isAddingCard && (
          <Button
            size="sm"
            leftIcon={<AddIcon boxSize={3} />}
            onClick={() => setIsAddingCard(true)}
            bg="white"
            border="1px solid #E5E7EB"
            color="#374151"
            fontSize="13px"
            fontWeight="500"
            h="32px"
            px={3}
            borderRadius="8px"
            _hover={{ bg: '#F9FAFB', borderColor: '#D1D5DB' }}
            transition="all 0.2s"
          >
            Add Link
          </Button>
        )}
      </Flex>

      {/* Inline Add Card Form */}
      <Collapse in={isAddingCard} animateOpacity>
        <Box bg="#F9FAFB" border="1px solid #E5E7EB" borderRadius="12px" p={4} mb={2}>
          <VStack spacing={3} align="stretch">
            <HStack justify="space-between" align="center">
              <Text fontSize="13px" fontWeight="600" color="#374151">
                Add New Link
              </Text>
              <IconButton
                icon={<CloseIcon />}
                size="xs"
                variant="ghost"
                onClick={() => {
                  setIsAddingCard(false);
                  setSelectedBoard('');
                  setSelectedCard('');
                  setSearchBoardQuery('');
                  setSearchCardQuery('');
                }}
                aria-label="Close"
              />
            </HStack>

            {/* Boards Search */}
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="#9CA3AF" boxSize={3} />
              </InputLeftElement>
              <Input
                placeholder="Search lists..."
                value={searchBoardQuery}
                onChange={(e) => setSearchBoardQuery(e.target.value)}
                bg="white"
                fontSize="13px"
                borderRadius="8px"
                borderColor="#E5E7EB"
                _focus={{ borderColor: '#3B82F6', boxShadow: '0 0 0 1px #3B82F6' }}
              />
            </InputGroup>

            {/* Boards List */}
            <Box
              maxH="200px"
              overflowY="auto"
              bg="white"
              borderRadius="8px"
              border="1px solid #E5E7EB"
            >
              {loadingBoards ? (
                <Flex justify="center" align="center" py={4}>
                  <Spinner size="sm" color="blue.500" />
                </Flex>
              ) : filteredBoards.length === 0 ? (
                <Text fontSize="12px" color="#9CA3AF" textAlign="center" py={4}>
                  No lists found
                </Text>
              ) : (
                <VStack spacing={0} align="stretch">
                  {filteredBoards.map((board, index) => (
                    <Box
                      key={board.id}
                      px={3}
                      py={2}
                      cursor="pointer"
                      bg={selectedBoard === board.id ? '#EFF6FF' : 'white'}
                      borderBottom={
                        index < filteredBoards.length - 1 ? '1px solid #F3F4F6' : 'none'
                      }
                      _hover={{ bg: '#F9FAFB' }}
                      onClick={() => handleBoardSelect(board.id)}
                      transition="all 0.15s"
                    >
                      <Text fontSize="13px" color="#374151" fontWeight="500" noOfLines={1}>
                        {board.title}
                      </Text>
                    </Box>
                  ))}
                </VStack>
              )}
            </Box>

            {selectedBoard && (
              <>
                <InputGroup size="sm">
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="#9CA3AF" boxSize={3} />
                  </InputLeftElement>
                  <Input
                    placeholder="Search cards..."
                    value={searchCardQuery}
                    onChange={(e) => setSearchCardQuery(e.target.value)}
                    bg="white"
                    fontSize="13px"
                    borderRadius="8px"
                    borderColor="#E5E7EB"
                    _focus={{ borderColor: '#3B82F6', boxShadow: '0 0 0 1px #3B82F6' }}
                  />
                </InputGroup>

                <Box
                  maxH="200px"
                  overflowY="auto"
                  bg="white"
                  borderRadius="8px"
                  border="1px solid #E5E7EB"
                >
                  {loadingCards ? (
                    <Flex justify="center" align="center" py={4}>
                      <Spinner size="sm" color="blue.500" />
                    </Flex>
                  ) : filteredCards.length === 0 ? (
                    <Text fontSize="12px" color="#9CA3AF" textAlign="center" py={4}>
                      No cards found
                    </Text>
                  ) : (
                    <VStack spacing={0} align="stretch">
                      {filteredCards.map((card, index) => (
                        <Box
                          key={card.id}
                          px={3}
                          py={2}
                          cursor="pointer"
                          bg={selectedCard === card.id ? '#EFF6FF' : 'white'}
                          borderBottom={
                            index < filteredCards.length - 1 ? '1px solid #F3F4F6' : 'none'
                          }
                          _hover={{ bg: '#F9FAFB' }}
                          onClick={() => setSelectedCard(card.id)}
                          transition="all 0.15s"
                        >
                          <Text fontSize="13px" color="#374151" fontWeight="500" noOfLines={1}>
                            {card.title}
                          </Text>
                          {card.listTitle && (
                            <Text fontSize="11px" color="#9CA3AF" mt={0.5}>
                              {card.listTitle}
                            </Text>
                          )}
                        </Box>
                      ))}
                    </VStack>
                  )}
                </Box>
              </>
            )}

            <HStack justify="flex-end" spacing={2} pt={1}>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setIsAddingCard(false);
                  setSelectedBoard('');
                  setSelectedCard('');
                  setSearchBoardQuery('');
                  setSearchCardQuery('');
                }}
                fontSize="13px"
                h="32px"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                colorScheme="blue"
                onClick={handleLinkCard}
                isDisabled={!selectedCard}
                fontSize="13px"
                h="32px"
              >
                Link Card
              </Button>
            </HStack>
          </VStack>
        </Box>
      </Collapse>

      {/* Confirmation Alert Dialog */}
      <AlertDialog
        isOpen={isAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsAlertOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm Link
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to link the card "{selectedCardTitle}"?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setIsAlertOpen(false)}>
                No
              </Button>
              <Button colorScheme="blue" onClick={confirmLinkCard} ml={3}>
                Yes
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {linkedItems.length > 0 ? (
        <VStack spacing={2} align="stretch">
          {linkedItems.map((item) => {
            const statusConfig = statusColors[item.status] || statusColors['active'];
            return (
              <Box
                key={item.id}
                bg="white"
                border="1px solid #E5E7EB"
                borderRadius="10px"
                p={2}
                cursor="pointer"
                onClick={() => handleCardClick(item.id, item.boardId)}
                _hover={{
                  borderColor: '#3B82F6',
                  shadow: 'md',
                  transform: 'translateY(-1px)',
                }}
                transition="all 0.2s"
              >
                <HStack align="start" spacing={2} w="full">
                  <Tooltip label={item.assignee.name} placement="top" hasArrow>
                    <Avatar
                      size="sm"
                      name={item.assignee.name}
                      src={item.assignee.avatar}
                      bg={item.assignee.bgColor}
                      color="white"
                      fontSize="10px"
                      fontWeight="600"
                      w="24px"
                      h="24px"
                      flexShrink={0}
                    />
                  </Tooltip>

                  <VStack align="start" spacing={1} flex="1" minW={0}>
                    <Tooltip
                      label={item.title}
                      placement="top"
                      hasArrow
                      bg="gray.700"
                      color="white"
                      fontSize="12px"
                    >
                      <Text fontSize="13px" fontWeight="500" color="#1F2937" noOfLines={1} w="full">
                        {item.title}
                      </Text>
                    </Tooltip>

                    <HStack spacing={2} align="center">
                      <HStack spacing={1.5} bg={statusConfig.bg} px={2} py={0.5} borderRadius="6px">
                        <Text
                          fontSize="11px"
                          fontWeight="600"
                          color={statusConfig.color}
                          textTransform="uppercase"
                          letterSpacing="0.3px"
                        >
                          {item.type}
                        </Text>
                      </HStack>

                      <HStack spacing={1.5} bg={statusConfig.bg} px={2} py={0.5} borderRadius="6px">
                        <Box w="6px" h="6px" borderRadius="full" bg={statusConfig.dot} />
                        <Text
                          fontSize="11px"
                          fontWeight="600"
                          color={statusConfig.color}
                          textTransform="capitalize"
                          letterSpacing="0.3px"
                        >
                          {statusConfig.label}
                        </Text>
                      </HStack>
                    </HStack>

                    <HStack spacing={2} fontSize="11px" color="#9CA3AF">
                      <Text>{item.id}</Text>
                      {item.assignee.name && item.assignee.name !== 'Unassigned' && (
                        <>
                          <Text>•</Text>
                          <Text noOfLines={1}>{item.assignee.name}</Text>
                        </>
                      )}
                    </HStack>
                  </VStack>
                </HStack>
              </Box>
            );
          })}
        </VStack>
      ) : (
        <Box
          textAlign="center"
          py={10}
          bg="#F9FAFB"
          borderRadius="12px"
          border="1px dashed #D1D5DB"
        >
          <VStack spacing={2}>
            <Text fontSize="40px" opacity="0.4">
              🔗
            </Text>
            <Text fontSize="14px" color="#6B7280" fontWeight="500">
              No linked items yet
            </Text>
            <Text fontSize="12px" color="#9CA3AF" maxW="200px">
              Connect related tasks to track dependencies
            </Text>
          </VStack>
        </Box>
      )}
    </VStack>
  );
};

export default LinkTab;
